import { useEffect, useRef } from "react";
import { placeholderImage } from "../config/imageConfig";
import { X } from "lucide-react";
import TryOnMirror from "./TryOnMirror";

/**
 * Try-on mode of a product page: the product photo and the try-on mirror,
 * side by side and the same size, spanning the page. The product details
 * move below (the page lays them out; see `.ob-pd-staged` in orbiee.css).
 *
 * @param {object} props
 * @param {string} props.productId
 * @param {string} [props.productTitle]
 * @param {string} props.image          Current product photo URL.
 * @param {number} [props.count]        Number of product photos.
 * @param {number} [props.index]        Current photo index.
 * @param {() => void} [props.onPrev]
 * @param {() => void} [props.onNext]
 * @param {string|null} [props.autoStart]  Render immediately in this mode (agent path).
 * @param {() => void} props.onExit     Leave try-on mode.
 */
export default function TryOnStage({
  productId,
  productTitle,
  image,
  count = 1,
  index = 0,
  onPrev,
  onNext,
  autoStart = null,
  onExit,
}) {
  const rootRef = useRef(null);

  // Bring the stage into view, clear of the fixed top bar: the agent can
  // start a try-on while the shopper is scrolled further down the page.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 84;
    if (Math.abs(top - window.scrollY) > 40) {
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }
  }, []);

  const seed = String(productId || "piece").replace(/\s+/g, "");

  return (
    <section ref={rootRef} className="ob-tryon-stage" aria-label="Virtual try-on">
      <div className="ob-tryon-stage-inner">
        {/* The piece's name is the stage's headline, in the page-title face:
            the stage replaces the gallery at the top of the page, so this is
            what tells the shopper which garment they are trying on. An h2,
            because the page's h1 (the same title) is still in the details. */}
        <div className="ob-tryon-head">
          <div className="ob-tryon-heading">
            <span className="ob-tryon-badge">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="5" y="2" width="14" height="17" rx="7" />
                <path d="M9 22h6M12 19v3" />
                <path d="M10 9a2 2 0 0 1 4 0" />
              </svg>
              Virtual try-on
            </span>
            {productTitle && (
              <h2 className="ob-display ob-tryon-title" title={productTitle}>
                {productTitle}
              </h2>
            )}
          </div>
          <button
            type="button"
            className="ob-tryon-exit"
            onClick={onExit}
            aria-label="Close virtual try-on"
          >
            <X size={13} />
            Done
          </button>
        </div>

        <div className="ob-tryon-panels">
          {/* The piece */}
          <figure className="ob-tryon-panel ob-tryon-panel-piece">
            <figcaption className="ob-tryon-cap">The piece</figcaption>
            <div className="ob-hero ob-stage-photo">
              <img
                src={image}
                alt={productTitle || "Product"}
                decoding="async"
                onError={(e) => {
                  e.target.src = placeholderImage(seed, 800, 1066);
                }}
              />
              {count > 1 && (
                <>
                  <button
                    type="button"
                    className="ob-heronav ob-heronav-prev"
                    onClick={onPrev}
                    aria-label="Previous image"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-[17px] h-[17px]"
                    >
                      <path d="m15 18-6-6 6-6" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="ob-heronav ob-heronav-next"
                    onClick={onNext}
                    aria-label="Next image"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-[17px] h-[17px]"
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </button>
                  <div className="ob-herodots">
                    {Array.from({ length: count }).map((_, i) => (
                      <span
                        key={i}
                        className={`ob-herodot ${i === index ? "ob-on" : ""}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </figure>

          {/* On you */}
          <figure className="ob-tryon-panel ob-tryon-panel-you">
            <figcaption className="ob-tryon-cap">On you</figcaption>
            <TryOnMirror productId={productId} autoStart={autoStart} />
          </figure>
        </div>
      </div>
    </section>
  );
}
